"use client";

import React, { useMemo } from "react";
import * as THREE from "three";

function canvasTex(draw: (ctx: CanvasRenderingContext2D, s: number) => void, size: number, repeat = true) {
  const c = document.createElement("canvas");
  c.width = size;
  c.height = size;
  const ctx = c.getContext("2d");
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.wrapS = tex.wrapT = repeat ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping;
  if (ctx) {
    draw(ctx, size);
    tex.needsUpdate = true;
  }
  return tex;
}

let stone: THREE.CanvasTexture | null = null;
let roof: THREE.CanvasTexture | null = null;
let wood: THREE.CanvasTexture | null = null;
let plaster: THREE.CanvasTexture | null = null;

/** Ashlar courses. Mid gray so a material color can tint it. */
export function stoneTex() {
  if (stone) return stone;
  stone = canvasTex((ctx, s) => {
    ctx.fillStyle = "#b7b1a6";
    ctx.fillRect(0, 0, s, s);
    const bh = 28;
    const bw = 56;
    let row = 0;
    for (let y = 2; y < s; y += bh) {
      const shift = row % 2 ? bw / 2 : 0;
      for (let x = -bw; x < s + bw; x += bw) {
        const n = ((x + 400) * 13 + row * 29) % 17;
        const g = 168 + (n % 7) * 8;
        ctx.fillStyle = `rgb(${g},${g - 4},${g - 10})`;
        ctx.fillRect(x + shift + 2, y, bw - 4, bh - 4);
        ctx.fillStyle = "rgba(255,255,255,0.18)";
        ctx.fillRect(x + shift + 2, y, bw - 4, 2);
        ctx.fillStyle = "rgba(30,26,22,0.72)";
        ctx.fillRect(x + shift + 2, y + bh - 7, bw - 4, 4);
      }
      row++;
    }
  }, 256);
  return stone;
}

/** Tile rows in grayscale. The roof color tints them terracotta, blue, or thatch. */
export function roofTex() {
  if (roof) return roof;
  roof = canvasTex((ctx, s) => {
    ctx.fillStyle = "#d9d9d9";
    ctx.fillRect(0, 0, s, s);
    const th = 18;
    const tw = 22;
    let row = 0;
    for (let y = 0; y < s; y += th) {
      const shift = row % 2 ? tw / 2 : 0;
      for (let x = -tw; x < s + tw; x += tw) {
        const n = ((x + 80) * 3 + row * 11) % 9;
        const g = 214 - n * 6;
        ctx.fillStyle = `rgb(${g},${g},${g})`;
        ctx.fillRect(x + shift, y, tw - 1, th - 3);
        ctx.fillStyle = "rgba(12,8,6,0.82)";
        ctx.fillRect(x + shift, y + th - 5, tw, 5);
        ctx.fillStyle = "rgba(40,32,24,0.35)";
        ctx.fillRect(x + shift + tw - 2, y, 2, th);
      }
      row++;
    }
  }, 128);
  return roof;
}

export function woodTex() {
  if (wood) return wood;
  wood = canvasTex((ctx, s) => {
    ctx.fillStyle = "#c8b49a";
    ctx.fillRect(0, 0, s, s);
    const pw = 22;
    for (let x = 0; x < s; x += pw) {
      const n = (x * 5) % 11;
      const g = 176 + n * 4;
      ctx.fillStyle = `rgb(${g},${g - 18},${g - 40})`;
      ctx.fillRect(x, 0, pw - 3, s);
      ctx.fillStyle = "rgba(40,24,12,0.55)";
      ctx.fillRect(x + pw - 3, 0, 3, s);
      ctx.fillStyle = "rgba(90,60,30,0.35)";
      ctx.beginPath();
      ctx.ellipse(x + 8, 40 + (n * 17) % (s - 20), 4, 3, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }, 128);
  return wood;
}

export function plasterTex() {
  if (plaster) return plaster;
  plaster = canvasTex((ctx, s) => {
    ctx.fillStyle = "#ead8b8";
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 900; i++) {
      const x = (i * 47) % s;
      const y = (i * 19) % s;
      ctx.fillStyle = i % 2 ? "rgba(120,90,50,0.18)" : "rgba(255,248,230,0.2)";
      ctx.fillRect(x, y, 2, 2);
    }
    ctx.fillStyle = "#3c2c1e";
    ctx.fillRect(0, 0, 18, s);
    ctx.fillRect(s - 18, 0, 18, s);
    ctx.fillRect(0, 0, s, 20);
    ctx.fillRect(0, s - 22, s, 22);
    ctx.fillRect(0, s * 0.48, s, 16);
    ctx.fillRect(s * 0.46, 0, 16, s);
    ctx.save();
    ctx.translate(36, s - 30);
    ctx.rotate(-0.85);
    ctx.fillRect(0, 0, 150, 14);
    ctx.restore();
  }, 256, false);
  return plaster;
}

const gables = new Map<string, THREE.BufferGeometry>();
const gableEnds = new Map<string, THREE.BufferGeometry>();

function gableEndGeometry(w: number, d: number, rise: number) {
  const key = `${w}|${d}|${rise}`;
  const hit = gableEnds.get(key);
  if (hit) return hit;
  const hw = w / 2;
  const hd = d / 2;
  const pos = [
    hw, 0, hd, hw, 0, -hd, hw, rise, 0,
    -hw, 0, -hd, -hw, 0, hd, -hw, rise, 0,
  ];
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.computeVertexNormals();
  gableEnds.set(key, g);
  return g;
}

export function gableGeometry(w: number, d: number, rise: number) {
  const key = `${w}|${d}|${rise}`;
  const hit = gables.get(key);
  if (hit) return hit;
  const hw = w / 2;
  const hd = d / 2;
  const pos = [
    -hw, 0, hd, hw, 0, hd, hw, rise, 0, -hw, 0, hd, hw, rise, 0, -hw, rise, 0,
    hw, 0, -hd, -hw, 0, -hd, -hw, rise, 0, hw, 0, -hd, -hw, rise, 0, hw, rise, 0,
  ];
  const uv = [
    0, 0, 2, 0, 2, 1, 0, 0, 2, 1, 0, 1,
    0, 0, 2, 0, 2, 1, 0, 0, 2, 1, 0, 1,
  ];
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  g.computeVertexNormals();
  gables.set(key, g);
  return g;
}

function Flag({ accent, y }: { accent: string; y: number }) {
  return (
    <group position={[0, y, 0]}>
      <mesh position={[0, 0.32, 0]} castShadow>
        <boxGeometry args={[0.05, 0.64, 0.05]} />
        <meshStandardMaterial color="#4a3828" roughness={0.85} />
      </mesh>
      <mesh position={[0.16, 0.52, 0]}>
        <boxGeometry args={[0.34, 0.18, 0.025]} />
        <meshStandardMaterial
          color={accent}
          emissive={accent}
          emissiveIntensity={0.4}
          roughness={0.55}
          side={THREE.DoubleSide}
        />
      </mesh>
    </group>
  );
}

export function GableRoof({
  w,
  d,
  rise,
  y,
  color = "#b54a2a",
}: {
  w: number;
  d: number;
  rise: number;
  y: number;
  color?: string;
}) {
  const geo = useMemo(() => gableGeometry(w, d, rise), [w, d, rise]);
  const ends = useMemo(() => gableEndGeometry(w, d, rise), [w, d, rise]);
  return (
    <group position={[0, y, 0]}>
      <mesh geometry={geo} castShadow>
        <meshStandardMaterial
          map={roofTex()}
          color={color}
          emissive={color}
          emissiveIntensity={0.28}
          roughness={0.78}
          metalness={0.02}
          side={THREE.DoubleSide}
        />
      </mesh>
      <mesh geometry={ends} castShadow>
        <meshStandardMaterial map={plasterTex()} roughness={0.88} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

function Door({ y, z, w = 0.38, h = 0.62 }: { y: number; z: number; w?: number; h?: number }) {
  return (
    <group position={[0, y, z]}>
      <mesh>
        <boxGeometry args={[w + 0.08, h + 0.08, 0.06]} />
        <meshStandardMaterial map={stoneTex()} color="#d9d3c8" roughness={0.9} />
      </mesh>
      <mesh position={[0, -0.02, 0.03]}>
        <boxGeometry args={[w, h, 0.04]} />
        <meshStandardMaterial map={woodTex()} color="#5a3a22" roughness={0.86} />
      </mesh>
    </group>
  );
}

function Window({ x, y, z }: { x: number; y: number; z: number }) {
  return (
    <mesh position={[x, y, z]}>
      <boxGeometry args={[0.28, 0.26, 0.05]} />
      <meshStandardMaterial color="#1c2c34" roughness={0.25} metalness={0.35} />
    </mesh>
  );
}

function LogWalls({ w, h, d, y }: { w: number; h: number; d: number; y: number }) {
  return (
    <group position={[0, y, 0]}>
      <mesh castShadow receiveShadow>
        <boxGeometry args={[w, h, d]} />
        <meshStandardMaterial map={woodTex()} color="#7a5a38" roughness={0.86} />
      </mesh>
      {Array.from({ length: 4 }, (_, i) => (
        <mesh key={i} position={[0, -h / 2 + 0.16 + i * (h / 4), d / 2 + 0.01]}>
          <boxGeometry args={[w * 0.96, 0.045, 0.02]} />
          <meshStandardMaterial color="#3a2818" roughness={0.9} />
        </mesh>
      ))}
    </group>
  );
}

function EaveTrim({ w, y, z, accent = "#2f4f86" }: { w: number; y: number; z: number; accent?: string }) {
  return (
    <mesh position={[0, y, z]}>
      <boxGeometry args={[w, 0.07, 0.08]} />
      <meshStandardMaterial color={accent} roughness={0.55} />
    </mesh>
  );
}

export function TimberHouse({ accent }: { accent: string }) {
  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]} receiveShadow>
        <circleGeometry args={[1.55, 16]} />
        <meshStandardMaterial color="#a67b52" roughness={1} />
      </mesh>
      <mesh receiveShadow position={[0, 0.06, 0]}>
        <boxGeometry args={[1.9, 0.12, 1.55]} />
        <meshStandardMaterial map={stoneTex()} color="#8a8478" roughness={0.92} />
      </mesh>
      <LogWalls w={1.6} h={0.95} d={1.22} y={0.58} />
      <GableRoof w={1.95} d={1.6} rise={0.72} y={1.05} color="#d4653a" />
      <EaveTrim w={1.85} y={1.08} z={0.72} />
      <EaveTrim w={1.85} y={1.08} z={-0.72} />
      <mesh castShadow position={[0.95, 0.42, 0.15]}>
        <boxGeometry args={[0.55, 0.55, 0.7]} />
        <meshStandardMaterial map={woodTex()} color="#6a4c32" roughness={0.88} />
      </mesh>
      <GableRoof w={0.7} d={0.85} rise={0.28} y={0.7} color="#5c5348" />
      <Door y={0.46} z={0.64} />
      <Window x={-0.4} y={0.68} z={0.64} />
      <Flag accent={accent} y={1.85} />
    </group>
  );
}

export function StoneHall({ accent }: { accent: string }) {
  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0.1]} receiveShadow>
        <circleGeometry args={[2.15, 18]} />
        <meshStandardMaterial color="#b8895a" roughness={1} />
      </mesh>
      <mesh receiveShadow position={[0, 0.08, 0.15]}>
        <boxGeometry args={[3.3, 0.16, 2.5]} />
        <meshStandardMaterial map={stoneTex()} color="#b7b1a4" roughness={0.92} />
      </mesh>
      <mesh castShadow receiveShadow position={[0, 0.78, 0]}>
        <boxGeometry args={[2.55, 1.25, 1.85]} />
        <meshStandardMaterial map={stoneTex()} color="#e7d7c0" roughness={0.88} />
      </mesh>
      <GableRoof w={3.05} d={2.35} rise={0.85} y={1.4} color="#d4653a" />
      <EaveTrim w={2.9} y={1.42} z={1.05} accent="#f0d78a" />
      <EaveTrim w={2.9} y={1.42} z={-1.05} accent="#f0d78a" />
      <mesh castShadow position={[0, 2.15, 0]}>
        <boxGeometry args={[1.15, 0.7, 1.05]} />
        <meshStandardMaterial map={stoneTex()} color="#efe4d2" roughness={0.86} />
      </mesh>
      <GableRoof w={1.4} d={1.25} rise={0.48} y={2.5} color="#c4542e" />
      <mesh castShadow position={[0, 2.95, 0]}>
        <boxGeometry args={[0.55, 0.4, 0.5]} />
        <meshStandardMaterial map={stoneTex()} color="#f3eadc" roughness={0.86} />
      </mesh>
      <mesh castShadow position={[1.55, 0.55, 0.15]}>
        <boxGeometry args={[0.7, 0.7, 1.15]} />
        <meshStandardMaterial map={stoneTex()} color="#e4d2b8" roughness={0.88} />
      </mesh>
      <GableRoof w={0.9} d={1.35} rise={0.42} y={0.9} color="#d4653a" />
      <mesh receiveShadow position={[0, 0.1, 1.15]}>
        <boxGeometry args={[0.9, 0.2, 0.45]} />
        <meshStandardMaterial map={stoneTex()} color="#c8c2b6" roughness={0.92} />
      </mesh>
      <Door y={0.62} z={0.98} w={0.5} h={0.85} />
      <Window x={-0.55} y={0.95} z={0.97} />
      <Window x={0.55} y={0.95} z={0.97} />
      <Window x={0} y={1.35} z={0.97} />
      <Flag accent={accent} y={3.35} />
      {Array.from({ length: 16 }, (_, i) => {
        const a = (i / 16) * Math.PI * 2;
        return (
          <mesh key={i} castShadow position={[Math.cos(a) * 2.35, 0.42, Math.sin(a) * 2.35]}>
            <boxGeometry args={[0.08, 0.78, 0.08]} />
            <meshStandardMaterial map={woodTex()} color="#5a4030" roughness={0.9} />
          </mesh>
        );
      })}
    </group>
  );
}

export function LongBarracks({ accent }: { accent: string }) {
  return (
    <group>
      <mesh receiveShadow position={[0, 0.06, 0]}>
        <boxGeometry args={[2.7, 0.12, 1.7]} />
        <meshStandardMaterial map={stoneTex()} color="#b3ab9e" roughness={0.92} />
      </mesh>
      <mesh castShadow receiveShadow position={[0, 0.62, 0]}>
        <boxGeometry args={[2.35, 1.05, 1.4]} />
        <meshStandardMaterial map={stoneTex()} color="#cabb9e" roughness={0.9} />
      </mesh>
      <GableRoof w={2.7} d={1.75} rise={0.55} y={1.14} color="#7a3a28" />
      <Door y={0.5} z={0.72} w={0.48} h={0.72} />
      <Window x={-0.7} y={0.7} z={0.72} />
      <Window x={0.7} y={0.7} z={0.72} />
      {[-0.85, 0.85].map((x) => (
        <mesh key={x} castShadow position={[x, 0.7, 0.78]}>
          <boxGeometry args={[0.08, 1.15, 0.08]} />
          <meshStandardMaterial map={woodTex()} color="#4a3424" roughness={0.86} />
        </mesh>
      ))}
      <Flag accent={accent} y={1.7} />
    </group>
  );
}

export function StoneKeep({ accent }: { accent: string }) {
  const towers: Array<[number, number]> = [
    [-1.15, -1.05],
    [1.15, -1.05],
    [-1.15, 1.05],
    [1.15, 1.05],
  ];
  return (
    <group>
      <mesh receiveShadow position={[0, 0.1, 0]}>
        <boxGeometry args={[3.3, 0.2, 3.05]} />
        <meshStandardMaterial map={stoneTex()} color="#a9a294" roughness={0.93} />
      </mesh>
      <mesh castShadow receiveShadow position={[0, 1.05, 0]}>
        <boxGeometry args={[2.4, 1.9, 2.15]} />
        <meshStandardMaterial map={stoneTex()} color="#d4cdc0" roughness={0.9} />
      </mesh>
      <GableRoof w={2.7} d={2.45} rise={0.7} y={2.0} color="#8a3828" />
      {towers.map(([x, z]) => (
        <group key={`${x}:${z}`} position={[x, 0, z]}>
          <mesh castShadow position={[0, 1.35, 0]}>
            <boxGeometry args={[0.72, 2.7, 0.72]} />
            <meshStandardMaterial map={stoneTex()} color="#c8c0b2" roughness={0.9} />
          </mesh>
          <mesh castShadow position={[0, 2.78, 0]}>
            <boxGeometry args={[0.86, 0.14, 0.86]} />
            <meshStandardMaterial map={stoneTex()} color="#ddd6ca" roughness={0.88} />
          </mesh>
          {[
            [-0.24, -0.24],
            [0.24, -0.24],
            [-0.24, 0.24],
            [0.24, 0.24],
          ].map(([cx, cz]) => (
            <mesh key={`${cx}:${cz}`} castShadow position={[cx, 2.98, cz]}>
              <boxGeometry args={[0.2, 0.28, 0.2]} />
              <meshStandardMaterial map={stoneTex()} color="#b3ab9e" roughness={0.9} />
            </mesh>
          ))}
        </group>
      ))}
      <Door y={0.7} z={1.1} w={0.52} h={0.9} />
      <Window x={-0.45} y={1.15} z={1.1} />
      <Window x={0.45} y={1.15} z={1.1} />
      <Flag accent={accent} y={2.85} />
    </group>
  );
}

export function GabledYard({
  accent,
  wall,
  roof,
}: {
  accent: string;
  wall: string;
  roof: string;
}) {
  return (
    <group>
      <mesh receiveShadow position={[0, 0.05, 0]}>
        <boxGeometry args={[1.9, 0.1, 1.6]} />
        <meshStandardMaterial map={stoneTex()} color="#b7b1a4" roughness={0.92} />
      </mesh>
      <mesh castShadow receiveShadow position={[0, 0.5, 0]}>
        <boxGeometry args={[1.6, 0.8, 1.3]} />
        <meshStandardMaterial map={woodTex()} color={wall} roughness={0.88} />
      </mesh>
      <GableRoof w={1.95} d={1.6} rise={0.5} y={0.9} color={roof} />
      <Door y={0.42} z={0.67} w={0.4} h={0.55} />
      <Flag accent={accent} y={1.45} />
    </group>
  );
}

export function StoneTower({ accent }: { accent: string }) {
  return (
    <group>
      <mesh castShadow receiveShadow position={[0, 1.15, 0]}>
        <cylinderGeometry args={[0.58, 0.72, 2.3, 10]} />
        <meshStandardMaterial map={stoneTex()} color="#d0c9bb" roughness={0.9} />
      </mesh>
      <mesh castShadow position={[0, 2.4, 0]}>
        <cylinderGeometry args={[0.78, 0.7, 0.22, 10]} />
        <meshStandardMaterial map={stoneTex()} color="#c4beb2" roughness={0.9} />
      </mesh>
      {Array.from({ length: 6 }, (_, i) => {
        const a = (i / 6) * Math.PI * 2;
        return (
          <mesh key={i} castShadow position={[Math.cos(a) * 0.48, 2.62, Math.sin(a) * 0.48]}>
            <boxGeometry args={[0.18, 0.26, 0.18]} />
            <meshStandardMaterial map={stoneTex()} color="#b3ab9e" roughness={0.9} />
          </mesh>
        );
      })}
      <GableRoof w={0.7} d={0.7} rise={0.32} y={2.55} color="#7a3424" />
      <Flag accent={accent} y={2.9} />
    </group>
  );
}
